import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext'; // Import useAuth
import { TextField, Button, Typography, Container, MenuItem, Select, InputLabel, FormControl, Autocomplete, CircularProgress } from '@mui/material';
import { debounce } from 'lodash';
import FormCard from './FormCard';

function UsuarioForm({ onUsuarioAdded, initialData, onCancelEdit, isChangePasswordMode = false, isLinkMode = false }) {
  const { user } = useAuth(); // Get user from context
  const [id, setId] = useState(null);
  const [empresa_id, setEmpresaId] = useState('');
  const [pessoa_id, setPessoaId] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState(''); // This will be new password in change password mode
  const [currentPassword, setCurrentPassword] = useState(''); // New state for current password
  const [tipo_usuario, setTipoUsuario] = useState('');
  const [empresas, setEmpresas] = useState([]);
  const [pessoaOptions, setPessoaOptions] = useState([]);
  const [selectedPessoa, setSelectedPessoa] = useState(null);
  const [loadingPessoas, setLoadingPessoas] = useState(false);

  const debouncedFetchPessoas = useMemo(() =>
    debounce((searchTerm) => {
      if (!empresa_id) return;
      setLoadingPessoas(true);
      api.get(`/pessoas?search=${encodeURIComponent(searchTerm)}&empresa_id=${empresa_id}`)
        .then(response => setPessoaOptions(response.data.data || response.data))
        .catch(error => {
          console.error('Erro ao buscar pessoas para usuário:', error);
        })
        .finally(() => setLoadingPessoas(false));
    }, 500),
    [empresa_id]
  );

  useEffect(() => {
    return () => {
      debouncedFetchPessoas.cancel();
    };
  }, [debouncedFetchPessoas]);

  useEffect(() => {
    const fetchAndSetSelectedPessoa = async (personId, empresaId) => {
      if (!personId) return;

      try {
        const response = await api.get(`/pessoas?id=${personId}&empresa_id=${empresaId}`);
        const item = (response.data.data || response.data)[0];

        if (item) {
          setSelectedPessoa(item);
          setPessoaOptions([item]);
        }
      } catch (error) {
        console.error(`Erro ao buscar pessoa com ID ${personId}:`, error);
      }
    };

    // Only fetch lists if not in change password mode
    if (!isChangePasswordMode) {
      // Carregar lista de empresas para o select apenas se o usuario for admin
      if (user && user.tipo_usuario === 'admin') {
        api.get('/empresas')
          .then(response => {
            setEmpresas(response.data.data || response.data); // Adjust for paginated or non-paginated response
          })
          .catch(error => {
            console.error('Erro ao buscar empresas:', error);
          });
      }
    }

    if (initialData) {
      setId(initialData.id);
      setEmpresaId(isLinkMode ? '' : initialData.empresa_id);
      setPessoaId(isLinkMode ? '' : initialData.pessoa_id);
      setEmail(initialData.email);
      setSenha(''); // N\u00E3o pre-preencher a senha em modo de edicao
      setTipoUsuario(isLinkMode ? '' : initialData.tipo_usuario);
      setCurrentPassword(''); // Clear current password on initial data load

      if (!isChangePasswordMode && !isLinkMode) {
        fetchAndSetSelectedPessoa(initialData.pessoa_id, initialData.empresa_id);
      }
    } else {
      clearForm();
      if (user?.tipo_usuario !== 'admin') setEmpresaId(user?.empresa_id || '');
    }
  }, [initialData, user, isChangePasswordMode, isLinkMode]);

  const clearForm = () => {
    setId(null);
    setEmpresaId('');
    setPessoaId('');
    setEmail('');
    setSenha('');
    setCurrentPassword(''); // Clear current password
    setTipoUsuario('');
    setPessoaOptions([]);
    setSelectedPessoa(null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (isChangePasswordMode) {
      // Handle password change
      if (!currentPassword || !senha) {
        alert('Por favor, preencha a senha atual e a nova senha.');
        return;
      }
      api.put(`/usuarios/change-password/${id}`, { currentPassword, newPassword: senha })
        .then(response => {
          console.log('Senha atualizada:', response.data);
          clearForm();
          onUsuarioAdded(); // Call the success callback
        })
        .catch(error => {
          console.error('Erro ao alterar senha:', error);
          alert(error.response?.data?.message || 'Erro ao alterar senha.');
        });
    } else {
      // Existing logic for creating/editing user
      const usuarioData = {
        empresa_id: user.tipo_usuario === 'admin' ? empresa_id : user.empresa_id, // Define empresa_id com base no tipo de usuario
        pessoa_id,
        email,
        senha: isLinkMode ? '' : (id && senha === '' ? '********' : senha),
        tipo_usuario: user.tipo_usuario === 'admin' ? tipo_usuario : initialData?.tipo_usuario || 'padrao', // Nao-admins n\u00E3o podem mudar o tipo
      };

      if (isLinkMode) {
        api.post('/usuarios', usuarioData)
          .then(() => {
            clearForm();
            onUsuarioAdded();
          })
          .catch(error => {
            console.error('Erro ao vincular condomínio:', error);
            alert(error.response?.data?.message || 'Erro ao vincular condomínio.');
          });
      } else if (id) {
        // Modo de edicao
        api.put(`/usuarios/${id}`, usuarioData)
          .then(response => {
            console.log('Usuario atualizado:', response.data);
            clearForm();
            onUsuarioAdded();
            onCancelEdit();
          })
          .catch(error => {
            console.error('Erro ao atualizar usuario:', error);
          });
      } else {
        // Modo de criacao
        api.post('/usuarios', usuarioData)
          .then(response => {
            console.log('Usuario adicionado:', response.data);
            clearForm();
            onUsuarioAdded();
          })
          .catch(error => {
            console.error('Erro ao adicionar usuario:', error);
          });
      }
    }
  };

  return (
    <Container maxWidth="sm">
      <FormCard component="form" onSubmit={handleSubmit}>
        <Typography variant="h5" gutterBottom>
          {isChangePasswordMode ? 'Alterar Senha' : (isLinkMode ? 'Vincular Condomínio' : (id ? 'Editar Usu\u00E1rio' : 'Adicionar Novo Usu\u00E1rio'))}
        </Typography>
        {(!isChangePasswordMode && user && user.tipo_usuario === 'admin') && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="empresa-select-label">Empresa</InputLabel>
            <Select
              labelId="empresa-select-label"
              value={empresa_id}
              label="Empresa"
              onChange={(e) => {
                setEmpresaId(e.target.value);
                setPessoaId('');
                setSelectedPessoa(null);
                setPessoaOptions([]);
              }}
              disabled={!!initialData && !isLinkMode}
            >
              {empresas.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {(!isChangePasswordMode) && (
          <Autocomplete
            id="pessoa-autocomplete-usuario"
            options={pessoaOptions}
            getOptionLabel={(option) => option.nome || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedPessoa}
            onChange={(event, newValue) => {
              setSelectedPessoa(newValue);
              setPessoaId(newValue ? newValue.id : '');
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
                if (newInputValue) {
                  debouncedFetchPessoas(newInputValue);
                } else {
                  setPessoaOptions(selectedPessoa ? [selectedPessoa] : []);
                }
              }
            }}
            disabled={!!initialData && !isLinkMode}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pessoa"
                variant="outlined"
                margin="normal"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}
        <TextField
          label="Email"
          variant="outlined"
          fullWidth
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          InputProps={{
            readOnly: isChangePasswordMode || isLinkMode,
          }}
        />
        {isChangePasswordMode && ( // Show current password field only in change password mode
          <TextField
            label="Senha Atual"
            variant="outlined"
            fullWidth
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            margin="normal"
            required
          />
        )}
        {!isLinkMode && <TextField
          label={isChangePasswordMode ? "Nova Senha" : "Senha"} // Change label based on mode
          variant="outlined"
          fullWidth
          type="password"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          margin="normal"
          required={!id || isChangePasswordMode}
        />}
        {(!isChangePasswordMode && user && user.tipo_usuario === 'admin') && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="tipo-usuario-select-label">Tipo de Usu\u00E1rio</InputLabel>
            <Select
              labelId="tipo-usuario-select-label"
              value={tipo_usuario}
              label="Tipo de Usu\u00E1rio"
              onChange={(e) => setTipoUsuario(e.target.value)}
            >
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="portaria">Portaria</MenuItem>
              <MenuItem value="sindico">Sindico</MenuItem>
              <MenuItem value="padrao">Padrao</MenuItem>
            </Select>
          </FormControl>
        )}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 2, mr: 2 }}
        >
          {isChangePasswordMode ? 'Alterar Senha' : (isLinkMode ? 'Vincular Condomínio' : (id ? 'Atualizar Usu\u00E1rio' : 'Adicionar Usu\u00E1rio'))}
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          sx={{ mt: 2 }}
          onClick={onCancelEdit}
        >
          Cancelar
        </Button>
      </FormCard>
    </Container>
  );
}

export default UsuarioForm;

